import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import {
  STATUS_COLORS,
  STATUS_LABELS,
  meAddAddress,
  meAddresses,
  meChangeSub,
  meDeleteAddress,
  meOrders,
  meSubscriptions,
} from "../lib/api";

function StatusPill({ status }) {
  return (
    <span
      className="inline-block px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: STATUS_COLORS[status]?.bg || "#eee", color: "#1A1A24" }}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [addForm, setAddForm] = useState({ label: "Home", address: "", sector: "", is_default: false });

  const load = async () => {
    try {
      const [o, s, a] = await Promise.all([meOrders(), meSubscriptions(), meAddresses()]);
      setOrders(o.orders || []);
      setSubs(s.subscriptions || []);
      setAddresses(a.addresses || []);
    } catch (e) {
      // handled by interceptor / redirect
    }
  };

  useEffect(() => { load(); }, []);

  const doSubAction = async (code, action) => {
    try {
      await meChangeSub(code, action);
      toast.success(`Subscription ${code}: ${action === "cancel" ? "cancelled" : action + "d"}`);
      load();
    } catch (e) {
      toast.error("Failed to update subscription");
    }
  };

  const addAddress = async (e) => {
    e.preventDefault();
    if (!addForm.address || !addForm.label) return;
    try {
      const r = await meAddAddress(addForm);
      setAddresses(r.addresses);
      setAddForm({ label: "Home", address: "", sector: "", is_default: false });
      toast.success("Address saved");
    } catch (e) {
      toast.error("Could not save address");
    }
  };

  const deleteAddress = async (id) => {
    try {
      const r = await meDeleteAddress(id);
      setAddresses(r.addresses);
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid="account-page">
      <Seo title="My Account" description="Your RIZ N WASH dashboard — orders, subscriptions and saved addresses." path="/account" noindex={true} />
      <Nav />
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline mb-2">My account</div>
            <h1 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
              Hi {user?.name?.split(" ")[0] || "there"} <span className="text-rw-blue">·</span>
            </h1>
            <p className="mt-2 text-rw-ink2 text-sm">
              {user?.email || user?.phone}
              {user?.provider === "google" && <span className="ml-2 inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full bg-rw-mint">Google</span>}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/book" className="btn-dark rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="account-book-btn">
              Book new pickup →
            </Link>
            <button
              onClick={logout}
              className="rounded-full border border-rw-ink/20 px-4 py-2.5 text-sm hover:bg-rw-bg3"
              data-testid="account-logout-btn"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-2 border-b border-rw-ink/10 pb-3">
          {[["orders", "My orders"], ["subs", "Subscriptions"], ["addr", "Saved addresses"]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-sm font-semibold rounded-full ${
                tab === id ? "bg-rw-ink text-white" : "text-rw-ink2 hover:bg-rw-bg3"
              }`}
              data-testid={`account-tab-${id}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "orders" && (
          <div className="mt-6 space-y-3" data-testid="account-orders">
            {orders.length === 0 && (
              <div className="rounded-2xl border border-dashed border-rw-ink/20 p-10 text-center text-rw-ink2">
                No orders yet. <Link to="/book" className="text-rw-ink font-semibold underline">Book your first pickup</Link>.
              </div>
            )}
            {orders.map((o) => (
              <div key={o.order_code} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5 flex flex-wrap justify-between gap-3" data-testid={`account-order-${o.order_code}`}>
                <div>
                  <div className="font-mono font-semibold text-lg">{o.order_code}</div>
                  <div className="text-xs text-rw-ink2 mt-1">
                    {o.pickup_date} · {o.pickup_slot} · {o.items.length} item{o.items.length > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl">₹{o.total}</div>
                  <div className="mt-2"><StatusPill status={o.status} /></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "subs" && (
          <div className="mt-6 space-y-3" data-testid="account-subs">
            {subs.length === 0 && (
              <div className="rounded-2xl border border-dashed border-rw-ink/20 p-10 text-center text-rw-ink2">
                Not subscribed yet. <Link to="/plans" className="text-rw-ink font-semibold underline">See weekly plans</Link>.
              </div>
            )}
            {subs.map((s) => (
              <div key={s.sub_code} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5" data-testid={`account-sub-${s.sub_code}`}>
                <div className="flex flex-wrap justify-between items-baseline gap-3">
                  <div>
                    <div className="font-mono font-semibold">{s.sub_code}</div>
                    <div className="font-display text-2xl mt-1">{s.plan_name} · ₹{s.weekly_total}/week</div>
                    <div className="text-xs text-rw-ink2 mt-1">Started {s.start_date}</div>
                  </div>
                  <span className="text-xs uppercase tracking-widest px-2 py-1 rounded-full bg-rw-bg3">{s.status}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {s.status === "active" && (
                    <button onClick={() => doSubAction(s.sub_code, "pause")} className="text-xs rounded-full border border-rw-ink/20 px-3 py-1.5" data-testid={`sub-pause-${s.sub_code}`}>Pause</button>
                  )}
                  {s.status === "paused" && (
                    <button onClick={() => doSubAction(s.sub_code, "resume")} className="text-xs rounded-full border border-rw-ink/20 px-3 py-1.5" data-testid={`sub-resume-${s.sub_code}`}>Resume</button>
                  )}
                  {s.status !== "cancelled" && (
                    <button onClick={() => doSubAction(s.sub_code, "cancel")} className="text-xs rounded-full bg-rw-peach/60 px-3 py-1.5" data-testid={`sub-cancel-${s.sub_code}`}>Cancel</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "addr" && (
          <div className="mt-6 grid md:grid-cols-2 gap-4" data-testid="account-addresses">
            {addresses.map((a) => (
              <div key={a.id} className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-5" data-testid={`address-${a.id}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-display text-lg">{a.label}</div>
                  {a.is_default && <span className="text-[10px] px-2 py-0.5 uppercase tracking-widest rounded-full bg-rw-mint">Default</span>}
                </div>
                <div className="text-sm text-rw-ink2 mt-1">{a.address}</div>
                {a.sector && <div className="text-xs text-rw-ink3">{a.sector}</div>}
                <button
                  onClick={() => deleteAddress(a.id)}
                  className="mt-4 text-xs text-rw-peach hover:underline"
                  data-testid={`delete-address-${a.id}`}
                >
                  Delete
                </button>
              </div>
            ))}
            <form onSubmit={addAddress} className="rounded-2xl border border-dashed border-rw-ink/20 p-5 space-y-3" data-testid="add-address-form">
              <div className="font-display text-lg">Add address</div>
              <input
                className="w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                placeholder="Label (Home, Office…)"
                value={addForm.label}
                onChange={(e) => setAddForm({ ...addForm, label: e.target.value })}
                data-testid="addr-input-label"
              />
              <textarea
                rows={2}
                className="w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                placeholder="Full address"
                value={addForm.address}
                onChange={(e) => setAddForm({ ...addForm, address: e.target.value })}
                data-testid="addr-input-address"
              />
              <input
                className="w-full rounded-md border border-rw-ink/20 bg-white px-3 py-2 text-sm rw-focus"
                placeholder="Sector / area"
                value={addForm.sector}
                onChange={(e) => setAddForm({ ...addForm, sector: e.target.value })}
                data-testid="addr-input-sector"
              />
              <label className="flex items-center gap-2 text-xs text-rw-ink2">
                <input
                  type="checkbox"
                  checked={addForm.is_default}
                  onChange={(e) => setAddForm({ ...addForm, is_default: e.target.checked })}
                  data-testid="addr-input-default"
                /> Set as default
              </label>
              <button
                type="submit"
                className="btn-dark w-full rounded-full py-2 text-sm font-semibold"
                data-testid="add-address-btn"
              >
                Save address
              </button>
            </form>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
